import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import styles from '../styles/ContactPage.module.css';

const SERVICE_OPTIONS = ['Photoshoot', 'Print order', 'Other'];

// ← Your Instagram handle, without the "@".
//    Leave it as '' and the Instagram option below is hidden entirely.
const INSTAGRAM_HANDLE = 'kangarooski211';

export default function ContactPage() {
  const [searchParams] = useSearchParams();
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);

  // Pre-select service type if arriving from a CTA link
  // e.g. /contact?type=shoot or /contact?type=print
  const typeParam = searchParams.get('type');
  const defaultService = typeParam === 'print' ? 'Print order'
    : typeParam === 'shoot' ? 'Photoshoot'
    : '';

  const [form, setForm] = useState({
    name: '',
    email: '',
    service: defaultService,
    message: '',
    company: '', // honeypot — real users leave this blank
  });

  const handleChange = e => {
    setForm(f => ({ ...f, [e.target.name]: e.target.value }));
  };

  const handleService = (option) => {
    setForm(f => ({ ...f, service: option }));
  };

  // ─────────────────────────────────────────
  // FORM SUBMISSION
  // Posts to the Cloudflare Pages Function at /api/contact,
  // which emails the inquiry via Resend.
  // ─────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSending(true);
    try {
      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Something went wrong.');
      }
      setSubmitted(true);
    } catch (err) {
      setError(
        INSTAGRAM_HANDLE
          ? "Sorry — your message couldn't be sent. Please reach me on Instagram using the link below."
          : "Sorry — your message couldn't be sent. Please try again in a moment."
      );
    } finally {
      setSending(false);
    }
  };

  return (
    <main>
      <div className={`container ${styles.contact}`}>
        <div className={styles.contact__inner}>

          {/* ── LEFT: INFO ─────────────────────── */}
          <div className={styles.contact__info}>
            <p className={`label ${styles.contact__label}`}>Get in touch</p>
            <h1 className={styles.contact__title}>
              Let's work<br />together
            </h1>
            <p className={styles.contact__desc}>
              {/* ← edit to match your availability and style */}
              Whether you have a shoot in mind, want a fine art print for
              your home, or just want to talk through an idea — I'd love
              to hear from you. I typically respond within 48 hours.
            </p>

            <div className={styles.contact__detail}>
              <p className={styles.contact__detail_item}>Based in the Bay Area</p>
              <p className={styles.contact__detail_item}>Available across most of Northern California</p>
              <p className={styles.contact__detail_item}>Turnaround time — 1 to 2 weeks, depending on gallery size</p>
            </div>
          </div>

          {/* ── RIGHT: FORM ────────────────────── */}
          <div className={styles.contact__right}>
          {submitted ? (
            <div className={styles.form_success}>
              Thank you — I'll be in touch soon.
            </div>
          ) : (
            <form className={styles.contact__form} onSubmit={handleSubmit} noValidate>

              {/* Honeypot — hidden from users, bots tend to fill it */}
              <input
                type='text'
                name='company'
                value={form.company}
                onChange={handleChange}
                tabIndex={-1}
                autoComplete='off'
                aria-hidden='true'
                style={{ position: 'absolute', left: '-9999px', width: 1, height: 1, opacity: 0 }}
              />

              {/* Name + Email row */}
              <div className={styles.form_row}>
                <div className={styles.form_field}>
                  <label htmlFor='name'>Name</label>
                  <input
                    id='name'
                    name='name'
                    type='text'
                    placeholder='Your name'
                    value={form.name}
                    onChange={handleChange}
                    required
                    autoComplete='name'
                  />
                </div>
                <div className={styles.form_field}>
                  <label htmlFor='email'>Email</label>
                  <input
                    id='email'
                    name='email'
                    type='email'
                    placeholder='your@email.com'
                    value={form.email}
                    onChange={handleChange}
                    required
                    autoComplete='email'
                  />
                </div>
              </div>

              {/* Service selector */}
              <div className={styles.form_field}>
                <label>I'm interested in</label>
                <div className={styles.form_options}>
                  {SERVICE_OPTIONS.map(option => (
                    <span
                      key={option}
                      className={`${styles.form_option} ${form.service === option ? styles['form_option--selected'] : ''}`}
                      onClick={() => handleService(option)}
                      role='button'
                      tabIndex={0}
                      onKeyDown={e => e.key === 'Enter' && handleService(option)}
                    >
                      {option}
                    </span>
                  ))}
                </div>
              </div>

              {/* Message */}
              <div className={styles.form_field}>
                <label htmlFor='message'>Message</label>
                <textarea
                  id='message'
                  name='message'
                  placeholder='Tell me about your project, timeline, or any questions you have…'
                  value={form.message}
                  onChange={handleChange}
                  required
                />
              </div>

              {error && (
                <p role='alert' style={{ color: '#a3402d', fontSize: 'var(--text-small)', margin: 0 }}>
                  {error}
                </p>
              )}

              <div className={styles.form_submit}>
                <button type='submit' className='btn' disabled={sending}>
                  {sending ? 'Sending…' : 'Send message'}
                </button>
              </div>

            </form>
          )}

          {/* ── ALTERNATIVE: INSTAGRAM ──────────
              Shown only once INSTAGRAM_HANDLE is set. */}
          {INSTAGRAM_HANDLE && (
            <div className={styles.alt_contact}>
              <div className={styles.alt_contact__divider}>
                <span>or</span>
              </div>

              <a
                className={styles.alt_contact__card}
                href={`https://instagram.com/${INSTAGRAM_HANDLE}`}
                target='_blank'
                rel='noopener noreferrer'
              >
                <svg
                  className={styles.alt_contact__icon}
                  viewBox='0 0 24 24'
                  fill='none'
                  stroke='currentColor'
                  strokeWidth='1.4'
                  aria-hidden='true'
                >
                  <rect x='2.5' y='2.5' width='19' height='19' rx='5.25' />
                  <circle cx='12' cy='12' r='4.4' />
                  <circle cx='17.6' cy='6.4' r='1.15' fill='currentColor' stroke='none' />
                </svg>

                <span className={styles.alt_contact__text}>
                  <span className={styles.alt_contact__title}>Message me on Instagram</span>
                </span>

                <span className={styles.alt_contact__arrow} aria-hidden='true'>→</span>
              </a>
            </div>
          )}

          </div>

        </div>
      </div>
    </main>
  );
}
